import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  FileSignature,
  Loader2,
  Mail,
  Plus,
  QrCode,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { isAdminRole } from "../../lib/roles";
import { Card, EmptyState, PageHeader, formatDateTime } from "../ui";

const STATUS_LABEL = {
  pending_otp: "Aguardando OTP",
  signed: "Assinado",
  revoked: "Revogado",
};
const STATUS_COLOR = {
  pending_otp: "bg-amber-50 text-amber-800 border-amber-300",
  signed: "bg-emerald-50 text-emerald-800 border-emerald-300",
  revoked: "bg-rose-50 text-rose-800 border-rose-300",
};
const StatusBadge = ({ status }) => (
  <span
    data-testid={`signer-status-${status}`}
    className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wide border ${
      STATUS_COLOR[status] || "bg-slate-100"
    }`}
  >
    {STATUS_LABEL[status] || status}
  </span>
);

const SIGNER_ROLES = new Set([
  "diretor",
  "conselheiro",
  "diretor_financeiro",
  "presidente",
]);

// ---------------------------------------------------------------------------
// LIST — /portal/assinador | /admin/assinaturas (admin)
// ---------------------------------------------------------------------------

export function SignerList() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const canSign = SIGNER_ROLES.has(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const url = isAdmin ? "/admin/signer/documents" : "/signer/documents";
  const base = isAdmin ? "/admin/assinaturas" : "/portal/assinador";
  const newLink = isAdmin ? "/admin/assinaturas/novo" : "/portal/assinador/novo";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(url);
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      className="max-w-6xl mx-auto px-4 md:px-8 py-8"
      data-testid="signer-list-page"
    >
      <PageHeader
        title={isAdmin ? "Assinaturas de Documentos" : "Assinador de Documentos"}
        subtitle={
          isAdmin
            ? "Visão administrativa de todas as assinaturas emitidas no portal."
            : "Faça upload de um PDF, posicione sua assinatura e confirme com o OTP enviado ao seu e-mail."
        }
        actions={
          canSign && (
            <Link
              to={newLink}
              data-testid="signer-new-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a]"
            >
              <Plus size={14} /> Novo documento
            </Link>
          )
        }
      />
      <Card className="mt-6 p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="inline animate-spin mr-2" size={16} /> Carregando…
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nenhum documento ainda."
            hint={isAdmin ? undefined : "Clique em Novo documento para começar."}
          />
        ) : (
          <table className="min-w-full text-sm" data-testid="signer-list-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Documento</th>
                {isAdmin && <th className="text-left px-3 py-2">Assinante</th>}
                <th className="text-left px-3 py-2">Páginas</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Data</th>
                <th className="text-right px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`signer-row-${r.id}`}
                >
                  <td className="px-3 py-2 font-medium">
                    <Link
                      to={`${base}/${r.id}`}
                      className="text-[#002776] hover:underline"
                    >
                      {r.original_filename}
                    </Link>
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-slate-600">
                      {r.owner_name}
                      <span className="block text-[10px] text-slate-400 uppercase tracking-wide">
                        {r.owner_role}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-2 text-slate-600">{r.pages_count}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {r.signed_at
                      ? formatDateTime(r.signed_at)
                      : formatDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`${base}/${r.id}`}
                      className="text-xs uppercase tracking-wide text-[#002776] hover:underline"
                    >
                      Abrir
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NEW — upload + position + OTP + download
// ---------------------------------------------------------------------------

export function SignerNew() {
  const navigate = useNavigate();
  const isAdminRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/admin/");
  const base = isAdminRoute ? "/admin/assinaturas" : "/portal/assinador";
  const [stage, setStage] = useState("upload"); // upload | position | otp | done
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [page, setPage] = useState(1);
  const [pos, setPos] = useState({ xPct: 0.1, yPct: 0.85, widthPct: 0.25 });
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [signing, setSigning] = useState(false);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/signer/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDoc(res.data);
      setStage("position");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const requestOtp = async () => {
    setSending(true);
    try {
      const res = await api.post(`/signer/${doc.id}/request-otp`);
      setOtpSentTo(res.data.sent_to);
      setStage("otp");
      toast.success(`Código enviado para ${res.data.sent_to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const confirmSign = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Informe o código de 6 dígitos.");
      return;
    }
    setSigning(true);
    try {
      const res = await api.post(`/signer/${doc.id}/sign`, {
        otp: otp.trim(),
        page,
        x_pct: pos.xPct,
        y_pct: pos.yPct,
        width_pct: pos.widthPct,
      });
      setDoc(res.data);
      setStage("done");
      toast.success("Documento assinado com sucesso.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  const downloadSigned = async () => {
    try {
      const res = await api.get(`/signer/${doc.id}/download?variant=signed`, {
        responseType: "blob",
      });
      triggerBlobDownload(res.data, doc.signed_filename || "documento_assinado.pdf");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div
      className="max-w-5xl mx-auto px-4 md:px-8 py-8"
      data-testid="signer-new-page"
    >
      <button
        onClick={() => navigate(base)}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-slate-500 hover:text-slate-800 mb-4"
      >
        <ArrowLeft size={14} /> Voltar
      </button>

      <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
        Assinador · Novo
      </div>
      <h1 className="mt-1 font-serif-display text-3xl">
        {stage === "upload" && "Envie o PDF"}
        {stage === "position" && "Posicione sua assinatura"}
        {stage === "otp" && "Confirme com o código do e-mail"}
        {stage === "done" && "Documento assinado"}
      </h1>

      <Stepper stage={stage} />

      {stage === "upload" && (
        <Card className="mt-6 p-6">
          <label className="block text-sm text-slate-700">
            Selecione um arquivo PDF (máx. 25MB, até 60 páginas):
          </label>
          <input
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="signer-file-input"
            className="mt-2 block w-full text-sm"
          />
          <div className="mt-4 flex justify-end">
            <button
              data-testid="signer-upload-btn"
              onClick={upload}
              disabled={!file || uploading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a] disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Upload size={14} />
              )}
              Enviar e abrir editor
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Antes de enviar, confirme que você já cadastrou sua "Minha
            Assinatura" no portal — ela será carimbada na posição que você
            escolher na próxima etapa.
          </p>
        </Card>
      )}

      {stage === "position" && doc && (
        <PositionPicker
          doc={doc}
          page={page}
          setPage={setPage}
          pos={pos}
          setPos={setPos}
          onContinue={requestOtp}
          sending={sending}
        />
      )}

      {stage === "otp" && doc && (
        <Card className="mt-6 p-6" data-testid="signer-otp-step">
          <div className="flex items-start gap-3">
            <Mail size={18} className="text-[#002776] mt-0.5" />
            <div>
              <p className="text-sm">
                Enviamos um código de 6 dígitos para{" "}
                <strong>{otpSentTo}</strong>. Ele é válido por 10 minutos.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Não compartilhe o código. Se não recebeu, verifique a caixa de
                spam ou solicite novamente.
              </p>
            </div>
          </div>
          <div className="mt-5 flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Código (OTP)
              </label>
              <input
                data-testid="signer-otp-input"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="mt-1 w-40 border border-slate-300 px-3 py-2 rounded-sm text-center tracking-[0.5em] font-mono text-lg"
                placeholder="______"
              />
            </div>
            <button
              onClick={requestOtp}
              className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:bg-slate-50"
            >
              <RefreshCw size={12} /> Reenviar
            </button>
            <button
              data-testid="signer-sign-btn"
              onClick={confirmSign}
              disabled={signing || otp.length !== 6}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a] disabled:opacity-50"
            >
              {signing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <FileSignature size={14} />
              )}
              Confirmar e assinar
            </button>
          </div>
        </Card>
      )}

      {stage === "done" && doc && (
        <Card className="mt-6 p-6" data-testid="signer-done-step">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={22} className="text-emerald-600 mt-0.5" />
            <div>
              <h3 className="font-serif-display text-xl">
                Documento assinado com sucesso
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                O PDF agora contém sua assinatura, bloco de metadados e um QR
                Code que leva à página pública de verificação.
              </p>
            </div>
          </div>
          <div className="mt-5 grid md:grid-cols-2 gap-3 text-sm">
            <KV label="Hash do original" value={doc.original_hash} mono />
            <KV label="Hash do assinado" value={doc.signed_hash} mono />
            <KV
              label="Verificação pública"
              value={`${window.location.origin}/verificar/${doc.verify_token}`}
              mono
            />
            <KV
              label="Assinado em"
              value={formatDateTime(doc.signed_at)}
            />
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              data-testid="signer-download-signed"
              onClick={downloadSigned}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a]"
            >
              <Download size={14} /> Baixar PDF assinado
            </button>
            <Link
              to={`/verificar/${doc.verify_token}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#002776] text-[#002776] text-[11px] uppercase tracking-[0.25em] hover:bg-[#002776] hover:text-white"
            >
              <QrCode size={14} /> Ver página pública
            </Link>
            <Link
              to={`${base}/${doc.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:bg-slate-50"
            >
              Ir para o documento
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
}

const KV = ({ label, value, mono }) => (
  <div className="border border-slate-200 rounded-sm p-3 bg-white">
    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
      {label}
    </div>
    <div
      className={`mt-1 text-xs break-all ${mono ? "font-mono" : ""} text-slate-800`}
    >
      {value || "—"}
    </div>
  </div>
);

const Stepper = ({ stage }) => {
  const steps = [
    { k: "upload", label: "Upload" },
    { k: "position", label: "Posição" },
    { k: "otp", label: "OTP" },
    { k: "done", label: "Pronto" },
  ];
  const idx = steps.findIndex((s) => s.k === stage);
  return (
    <ol className="mt-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em]">
      {steps.map((s, i) => (
        <li key={s.k} className="flex items-center gap-3">
          <span
            className={`w-6 h-6 flex items-center justify-center rounded-full border ${
              i <= idx
                ? "bg-[#002776] text-white border-[#002776]"
                : "bg-white text-slate-400 border-slate-300"
            }`}
          >
            {i + 1}
          </span>
          <span
            className={i <= idx ? "text-slate-800" : "text-slate-400"}
          >
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <span className="w-8 h-px bg-slate-300" />
          )}
        </li>
      ))}
    </ol>
  );
};

// ---------------------------------------------------------------------------
// Position picker — click to place signature on page preview
// ---------------------------------------------------------------------------

function PositionPicker({ doc, page, setPage, pos, setPos, onContinue, sending }) {
  const [pageUrl, setPageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  // Load page as blob (so Authorization header can be sent)
  useEffect(() => {
    let revoke;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/signer/${doc.id}/page/${page}`, {
          responseType: "blob",
        });
        const url = URL.createObjectURL(res.data);
        setPageUrl(url);
        revoke = () => URL.revokeObjectURL(url);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      if (revoke) revoke();
    };
  }, [doc.id, page]);

  const updateFromEvent = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    let xPct = (clientX - rect.left) / rect.width;
    let yPct = (clientY - rect.top) / rect.height;
    xPct = Math.max(0, Math.min(0.98, xPct));
    yPct = Math.max(0.02, Math.min(0.98, yPct));
    setPos((p) => ({ ...p, xPct, yPct }));
  };

  const onPointerDown = (e) => {
    draggingRef.current = true;
    updateFromEvent(e);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current) updateFromEvent(e);
  };
  const onPointerUp = () => {
    draggingRef.current = false;
  };

  const pageOptions = useMemo(
    () => Array.from({ length: doc.pages_count }, (_, i) => i + 1),
    [doc.pages_count]
  );

  return (
    <Card className="mt-6 p-5" data-testid="signer-position-step">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-serif-display text-xl">
            Clique ou arraste no documento para posicionar
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            O retângulo azul representa onde sua assinatura será carimbada.
            Também aparece um QR code ao lado dela, apontando para a página
            pública de verificação.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
              Página
            </label>
            <select
              data-testid="signer-page-select"
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
              className="mt-1 w-24 border border-slate-300 px-2 py-2 rounded-sm bg-white"
            >
              {pageOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
              Largura
            </label>
            <input
              type="range"
              min="0.12"
              max="0.5"
              step="0.01"
              value={pos.widthPct}
              onChange={(e) =>
                setPos((p) => ({ ...p, widthPct: Number(e.target.value) }))
              }
              className="mt-2 w-40"
              data-testid="signer-width-range"
            />
          </div>
        </div>
      </div>

      <div
        ref={containerRef}
        className="mt-4 relative bg-slate-100 border border-slate-300 select-none overflow-hidden"
        style={{ aspectRatio: `${doc.page_width} / ${doc.page_height}` }}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchStart={onPointerDown}
        onTouchMove={onPointerMove}
        onTouchEnd={onPointerUp}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm">
            <Loader2 className="animate-spin mr-2" size={16} /> Carregando página…
          </div>
        )}
        {pageUrl && (
          <img
            src={pageUrl}
            alt={`página ${page}`}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          />
        )}
        {/* Signature overlay */}
        <div
          data-testid="signer-overlay"
          className="absolute border-2 border-[#002776] bg-[#002776]/15 flex items-center justify-center text-[10px] uppercase tracking-wide text-[#002776] font-semibold pointer-events-none"
          style={{
            left: `${pos.xPct * 100}%`,
            top: `${pos.yPct * 100}%`,
            width: `${pos.widthPct * 100}%`,
            height: `${pos.widthPct * 100 * 0.32}%`,
          }}
        >
          Sua assinatura
        </div>
      </div>
      <div className="mt-4 flex justify-between items-center text-xs text-slate-500">
        <span>
          Posição: x={Math.round(pos.xPct * 100)}% · y=
          {Math.round(pos.yPct * 100)}% · largura={Math.round(pos.widthPct * 100)}%
        </span>
        <button
          data-testid="signer-request-otp-btn"
          onClick={onContinue}
          disabled={sending}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a] disabled:opacity-50"
        >
          {sending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Mail size={14} />
          )}
          Enviar OTP e continuar
        </button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Detail — view, download, revoke (admin)
// ---------------------------------------------------------------------------

export function SignerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const base = isAdmin ? "/admin/assinaturas" : "/portal/assinador";
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/signer/${id}`);
      setDoc(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      navigate(base);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, base]);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (variant) => {
    try {
      const res = await api.get(
        `/signer/${id}/download?variant=${variant}`,
        { responseType: "blob" }
      );
      const name =
        variant === "signed"
          ? doc.signed_filename || "documento_assinado.pdf"
          : doc.original_filename || "documento.pdf";
      triggerBlobDownload(res.data, name);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const revoke = async () => {
    const reason = window.prompt(
      "Informe o motivo da revogação (mínimo 3 caracteres):"
    );
    if (!reason || reason.trim().length < 3) return;
    setRevoking(true);
    try {
      const res = await api.post(`/admin/signer/${id}/revoke`, {
        reason: reason.trim(),
      });
      setDoc(res.data);
      toast.info("Assinatura revogada.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setRevoking(false);
    }
  };

  if (loading || !doc) {
    return (
      <div className="p-10 text-center text-slate-500">
        <Loader2 className="inline animate-spin mr-2" size={16} /> Carregando…
      </div>
    );
  }

  const perms = doc.permissions || {};
  const verifyUrl = `${window.location.origin}/verificar/${doc.verify_token}`;

  return (
    <div
      className="max-w-5xl mx-auto px-4 md:px-8 py-8"
      data-testid="signer-detail-page"
    >
      <button
        onClick={() => navigate(base)}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-slate-500 hover:text-slate-800 mb-4"
      >
        <ArrowLeft size={14} /> Voltar
      </button>
      <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
        Assinador · Documento
      </div>
      <div className="flex items-start justify-between gap-3 flex-wrap mt-1">
        <h1 className="font-serif-display text-3xl break-all">
          {doc.original_filename}
        </h1>
        <StatusBadge status={doc.status} />
      </div>

      <Card className="mt-6 p-5 grid md:grid-cols-2 gap-3 text-sm">
        <KV label="Assinante" value={`${doc.owner_name} · ${doc.owner_role}`} />
        <KV label="Páginas" value={doc.pages_count} />
        <KV label="Hash do original" value={doc.original_hash} mono />
        <KV label="Hash do assinado" value={doc.signed_hash || "—"} mono />
        <KV label="Assinado em" value={formatDateTime(doc.signed_at) || "—"} />
        <KV label="Verificação pública" value={verifyUrl} mono />
        {doc.status === "revoked" && (
          <>
            <KV
              label="Revogado em"
              value={formatDateTime(doc.revoked_at)}
            />
            <KV label="Motivo" value={doc.revoked_reason || "—"} />
          </>
        )}
      </Card>

      <div className="mt-6 flex flex-wrap gap-2">
        {perms.can_download_signed && (
          <button
            data-testid="signer-detail-download-signed"
            onClick={() => download("signed")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a]"
          >
            <Download size={14} /> Baixar PDF assinado
          </button>
        )}
        {perms.can_download_original && (
          <button
            onClick={() => download("original")}
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:bg-slate-50"
          >
            <Download size={14} /> Baixar original
          </button>
        )}
        <Link
          to={`/verificar/${doc.verify_token}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#002776] text-[#002776] text-[11px] uppercase tracking-[0.25em] hover:bg-[#002776] hover:text-white"
        >
          <QrCode size={14} /> Página pública de verificação
        </Link>
        {perms.can_revoke && (
          <button
            data-testid="signer-detail-revoke"
            onClick={revoke}
            disabled={revoking}
            className="inline-flex items-center gap-2 px-4 py-2 border border-rose-400 text-rose-700 text-[11px] uppercase tracking-[0.25em] hover:bg-rose-50 disabled:opacity-50"
          >
            {revoking ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <ShieldAlert size={14} />
            )}
            Revogar assinatura
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public verify page — /verificar/:token (no auth needed)
// ---------------------------------------------------------------------------

export function VerifyPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/verify/signature/${token}`);
        setData(res.data);
      } catch (e) {
        setError(
          e?.response?.status === 404
            ? "Assinatura não encontrada ou inválida."
            : formatApiErrorDetail(e?.response?.data?.detail)
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const valid = data?.status === "signed";
  const revoked = data?.status === "revoked";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div
        className="w-full max-w-xl bg-white rounded-sm shadow-sm border border-slate-200"
        data-testid="verify-page"
      >
        <div className="p-5 border-b border-slate-200 flex items-center gap-3">
          <ShieldCheck size={24} className="text-[#002776]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Verificação de Assinatura
            </div>
            <h1 className="font-serif-display text-xl">Portal ABLEMP</h1>
          </div>
        </div>

        {loading && (
          <div className="p-10 text-center text-slate-500">
            <Loader2 className="inline animate-spin mr-2" size={16} />
            Verificando…
          </div>
        )}

        {!loading && error && (
          <div className="p-6" data-testid="verify-error">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-rose-50 border border-rose-300 text-rose-800 text-xs uppercase tracking-wide">
              <X size={14} /> {error}
            </div>
          </div>
        )}

        {!loading && data && (
          <div className="p-6" data-testid={`verify-status-${data.status}`}>
            <div
              className={`inline-flex items-center gap-2 px-3 py-1 border text-xs uppercase tracking-wide ${
                valid
                  ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                  : revoked
                  ? "bg-rose-50 border-rose-300 text-rose-800"
                  : "bg-amber-50 border-amber-300 text-amber-800"
              }`}
            >
              {valid && <CheckCircle2 size={14} />}
              {revoked && <ShieldAlert size={14} />}
              {valid
                ? "Assinatura válida"
                : revoked
                ? "Assinatura revogada"
                : data.status}
            </div>

            <dl className="mt-5 grid gap-3 text-sm">
              <KVRow label="Assinante" value={data.signer_name} />
              <KVRow label="Função" value={data.signer_role} />
              <KVRow label="E-mail" value={data.signer_email_masked} />
              <KVRow
                label="Documento"
                value={data.original_filename}
              />
              <KVRow
                label="Hash do original (SHA-256)"
                value={data.original_hash}
                mono
              />
              {data.signed_hash && (
                <KVRow
                  label="Hash do assinado (SHA-256)"
                  value={data.signed_hash}
                  mono
                />
              )}
              {data.signed_at && (
                <KVRow
                  label="Assinado em"
                  value={formatDateTime(data.signed_at)}
                />
              )}
              {revoked && (
                <>
                  <KVRow
                    label="Revogado em"
                    value={formatDateTime(data.revoked_at)}
                  />
                  <KVRow label="Motivo" value={data.revoked_reason} />
                  <KVRow label="Revogado por" value={data.revoked_by_name} />
                </>
              )}
            </dl>

            <p className="mt-6 text-[11px] text-slate-500">
              Esta página pública permite conferir que o documento foi
              carimbado digitalmente pelo portal da ABLEMP, usando a
              identidade do assinante confirmada por OTP enviado ao e-mail
              dele no momento da assinatura.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const KVRow = ({ label, value, mono }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 border-b border-slate-100 pb-2 last:border-b-0 last:pb-0">
    <dt className="min-w-[160px] text-[10px] uppercase tracking-[0.22em] text-slate-500">
      {label}
    </dt>
    <dd
      className={`text-slate-800 break-all ${mono ? "font-mono text-xs" : ""}`}
    >
      {value || "—"}
    </dd>
  </div>
);

// Trigger a blob download reliably across browsers (Safari/iOS/Chromium).
// 1. Force a known blob type when the server doesn't send one.
// 2. Append the anchor to the DOM before clicking (required by Firefox).
// 3. Defer the revokeObjectURL — some browsers cancel the download if the
//    URL is revoked synchronously after click().
function triggerBlobDownload(data, filename) {
  const blob = data instanceof Blob ? data : new Blob([data], { type: "application/pdf" });
  const typedBlob = blob.type ? blob : new Blob([blob], { type: "application/pdf" });
  const url = window.URL.createObjectURL(typedBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "arquivo";
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Defer cleanup so Safari/iOS can finish the download.
  setTimeout(() => {
    a.remove();
    window.URL.revokeObjectURL(url);
  }, 2000);
}
